import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  createMessage,
  fetchConcierge,
  logoutConcierge,
  sendConciergeActivity,
  replyRatingFeedback,
  fetchConciergeAssignedLead,
} from 'services/api/concierge.api.service';
import callApiService from 'services/api/call.api.service';
import { setIsAuthed, setLoading, setNotification } from 'state/app.state';
import { apiFetchLead, apiMarkLeadAsDone } from 'state/lead.state';
import { apiErrorNotification, sendWebNotification } from 'utils/functions';
import {
  ThunkAPI,
  Concierge,
  ConciergeRatingCategory,
  CommunicationProviders,
  CommunicationProvider,
} from 'typings';
import { CallToken, CallTokens, RatingFeedbackResponse } from 'typings/api/concierge';
import { apiChangeAvailability } from 'state/common.actions';

interface ConciergeState {
  entity: Concierge | null;
  isActive: boolean;
  isOnCall: boolean;
  completedOneLead: boolean;
  isAwaitingNewLead: boolean;
  isEngagedWithSystemPresence: boolean;
  isFetchingCallToken: boolean;
  callTokens: CallTokens;
  assignedLeadId: null | string;
  assignedLeadIcon: string;
  isNewDistribution: boolean;
  hasActivity: boolean;
  isSubscribedToLdeBroadcastChannel: boolean;
  mayHaveMissedBroadcastEvents: boolean;
  isSubmittingFeedbackResponse: boolean;
  conciergeRatingCategories: ConciergeRatingCategory[];
}

/**
 * Asynchronous Actions (thunks)
 */
export const apiFetchConciergeProfile = createAsyncThunk(
  'concierge/apiFetchConciergeProfile',
  async () => {
    return await fetchConcierge();
  }
);

export const apiFetchAssignedLead = createAsyncThunk('concierge/apiFetchAssignedLead', async () => {
  return await fetchConciergeAssignedLead();
});

export const apiFetchCallToken = createAsyncThunk(
  'concierge/apiFetchCallToken',
  async (
    data: {
      communicationProvider: CommunicationProvider;
      communicationProviderAccountId: string | null;
    },
    thunkApi: ThunkAPI
  ): Promise<CallToken | undefined> => {
    try {
      const token = await callApiService.fetchCallToken(
        data.communicationProvider,
        data.communicationProviderAccountId
      );

      return token;
    } catch (error) {
      return thunkApi.rejectWithValue({ error: 'Fail' });
    }
  }
);

export const conciergeLogout = createAsyncThunk(
  'concierge/logout',
  async (complete: boolean = false, thunkApi: ThunkAPI) => {
    try {
      if (complete) {
        await logoutConcierge();
      }
    } finally {
      thunkApi.dispatch(setIsAuthed(false));
    }

    return true;
  }
);

export const sendMessageToLead = createAsyncThunk(
  'concierge/message-lead',
  async (message: any, thunkApi: ThunkAPI) => {
    try {
      thunkApi.dispatch(setLoading(true));
      await createMessage(message.type, message.data);

      return null;
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    } finally {
      thunkApi.dispatch(setLoading(false));
    }
  }
);

export const apiActivityPing = createAsyncThunk(
  'concierge/apiActivityPing',
  async (sessionExpiringSoon: boolean, thunkApi: ThunkAPI) => {
    try {
      const { hasActivity, isOnCall, assignedLeadId, isActive } = thunkApi.getState().concierge;
      // we will continue sending activity pings when concierge is inactive
      const shouldSendActivityPing = hasActivity || isOnCall || !isActive;

      if (shouldSendActivityPing) {
        await sendConciergeActivity();
      }

      if (!shouldSendActivityPing && assignedLeadId && sessionExpiringSoon) {
        sendWebNotification(
          'Inactivity warning',
          'You are about to be logged out of the Verse Console due to inactivity.'
        );
      }
    } catch {
      // pass
    }
  }
);

export const apiSubmitRatingFeedbackResponse = createAsyncThunk(
  'concierge/apiSubmitRatingFeedbackResponse',
  async (payload: RatingFeedbackResponse, thunkApi: ThunkAPI) => {
    try {
      await replyRatingFeedback(payload);
    } catch (error) {
      thunkApi.dispatch(setNotification(apiErrorNotification(error)));
      return thunkApi.rejectWithValue({ error: 'Fail' });
    }
  }
);

/* Concierge State Slice */
const conciergeInitialState: ConciergeState = {
  entity: null,
  isActive: false,
  isOnCall: false,
  completedOneLead: false,
  isAwaitingNewLead: false,
  isEngagedWithSystemPresence: false,
  isFetchingCallToken: false,
  callTokens: { twilio: {}, bandwidth: undefined },
  assignedLeadId: '',
  assignedLeadIcon: '',
  isNewDistribution: false,
  hasActivity: false,
  mayHaveMissedBroadcastEvents: false,
  isSubscribedToLdeBroadcastChannel: false,
  isSubmittingFeedbackResponse: false,
  conciergeRatingCategories: [],
};

export const conciergeSlice = createSlice({
  name: 'concierge',
  initialState: conciergeInitialState,
  reducers: {
    setConciergeOnCall: (state, action: PayloadAction<boolean>) => {
      state.isOnCall = action.payload;
    },
    setConciergeEntity: (state, action) => {
      state.entity = action.payload;
    },
    setSystemPresenceEngagement: (state, action: PayloadAction<boolean>) => {
      state.isEngagedWithSystemPresence = action.payload;
    },
    setAssignedLeadId: (state, action: PayloadAction<string>) => {
      state.assignedLeadId = action.payload;
      state.isNewDistribution = true;
    },
    setAssignedLeadIcon: (state, action: PayloadAction<string>) => {
      state.assignedLeadIcon = action.payload;
    },
    setConciergeActivity: (state, action: PayloadAction<boolean>) => {
      state.hasActivity = action.payload;
    },
    setIsSubscribedToLdeBroadcastChannel: (state, action: PayloadAction<boolean>) => {
      state.isSubscribedToLdeBroadcastChannel = action.payload;
    },
    setMayHaveMissedBroadcastEvents: (state, action: PayloadAction<boolean>) => {
      state.mayHaveMissedBroadcastEvents = action.payload;
    },
  },
  extraReducers: {
    [apiFetchConciergeProfile.fulfilled.toString()]: (state, action: PayloadAction<Concierge>) => {
      state.entity = action.payload;
      state.assignedLeadId = action.payload?.assignedLead?.id ?? '';
      state.isActive = action.payload?.isActive;
      state.conciergeRatingCategories = action.payload?.conciergeRatingCategories ?? [];

      // reset when fetching concierge profile
      state.mayHaveMissedBroadcastEvents = false;
    },
    [apiFetchAssignedLead.fulfilled.toString()]: (state, action) => {
      state.assignedLeadId = action.payload?.assignedLead?.id ?? '';
    },
    [conciergeLogout.fulfilled.toString()]: (state) => {
      state.entity = null;
    },
    [apiChangeAvailability.pending.toString()]: (state, action) => {
      state.isActive = action.meta.arg?.isActive;
    },
    [apiChangeAvailability.fulfilled.toString()]: (state, action) => {
      state.entity = { ...state.entity, ...action.payload };
      state.isActive = action.payload?.isActive;

      // if concierge is not active, we set assignedLeadId to null so
      // so that when they are redirected back to the home page, they
      // are not automatically redirected to the assigned lead
      if (state.isActive === false) {
        state.assignedLeadId = null;

        // this clears the 'Nice Work' text on the home page
        state.completedOneLead = false;
      }
    },
    [apiChangeAvailability.rejected.toString()]: (state, action) => {
      state.isActive = !action.meta.arg?.isActive;
    },
    [apiFetchLead.fulfilled.toString()]: (state) => {
      state.isAwaitingNewLead = false;

      // reset when loading a lead
      state.mayHaveMissedBroadcastEvents = false;
      state.isNewDistribution = false;
    },
    [apiMarkLeadAsDone.pending.toString()]: (state) => {
      state.isAwaitingNewLead = true;

      // reset when dismissing a lead
      state.mayHaveMissedBroadcastEvents = false;

      // this shows the 'Nice Work' text on the home page
      state.completedOneLead = true;
    },
    [apiFetchCallToken.pending.toString()]: (state) => {
      state.isFetchingCallToken = true;
    },
    [apiFetchCallToken.fulfilled.toString()]: (state, action: PayloadAction<CallToken>) => {
      switch (action.payload.communicationProvider) {
        case CommunicationProviders.TWILIO:
          state.callTokens.twilio[action.payload.communicationProviderAccountId ?? ''] =
            action.payload;
          break;
        case CommunicationProviders.BANDWIDTH:
          state.callTokens.bandwidth = action.payload;
          break;
      }

      state.isFetchingCallToken = false;
    },
    'call/setCallType': (state, action) => {
      state.isOnCall = action.payload !== null;
    },
    'call/setEndCall': (state) => {
      state.isOnCall = false;
    },
    [apiActivityPing.fulfilled.toString()]: (state) => {
      state.hasActivity = false;
    },
    [apiActivityPing.rejected.toString()]: (state) => {
      state.hasActivity = false;
    },
    [apiSubmitRatingFeedbackResponse.pending.toString()]: (state) => {
      state.isSubmittingFeedbackResponse = true;
    },
    [apiSubmitRatingFeedbackResponse.fulfilled.toString()]: (state) => {
      state.isSubmittingFeedbackResponse = false;
    },
    [apiSubmitRatingFeedbackResponse.rejected.toString()]: (state) => {
      state.isSubmittingFeedbackResponse = false;
    },
  },
});

export const {
  setConciergeOnCall,
  setConciergeEntity,
  setSystemPresenceEngagement,
  setAssignedLeadId,
  setAssignedLeadIcon,
  setConciergeActivity,
  setIsSubscribedToLdeBroadcastChannel,
  setMayHaveMissedBroadcastEvents,
} = conciergeSlice.actions;

export default conciergeSlice.reducer;
